import { useState, useEffect } from "react";
import {
  fetchAllEvents,
  createEventInFirestore,
  fetchAllCommunities,
  createCommunity,
  fetchUserMatches,
  saveMatchToFirestore,
  toggleEventAttendance,
} from "../services/meetlyService";
import { auth, db } from "../services/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";

// Shared "blank" profile shape, used whenever there's no signed-in user yet
// (initial state, no Firestore profile found, or after logout). Kept in one
// place so the three call sites can't drift out of sync with each other.
const BLANK_USER_DATA = {
  name: "",
  email: "",
  birthDate: "",
  gender: "",
  avatar: "Mateo",
  avatarStyle: "open-peeps", // Default diverse cheerful style
  interests: [],
  joinedEvents: [],
  joinedCommunities: [],
  bio: "",
};

export function useAppLogic() {
  // This hook is the single source of truth for the whole app: it owns the
  // current screen ("appState"), the logged-in user's profile, and the
  // three main Firestore-backed collections (events, communities, matches).
  // App.tsx renders based on what this hook returns and exposes the pieces
  // it needs via UserContext/DataContext instead of prop-drilling them.

  // --- STATE MANAGEMENT ---
  const [appState, setAppState] = useState("splash");
  const [userData, setUserData] = useState(BLANK_USER_DATA);

  const [events, setEvents] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);

  // Store initial target state resolved by Firebase auth to transition smoothly after splash
  const [pendingNextState, setPendingNextState] = useState(null);

  // --- GLOBAL DATA LOADER ---
  // Fetches events, communities, and (if logged in) the user's saved matches
  // in one go. Called both after login/onboarding and whenever the app
  // enters the main view with no events loaded yet (see effect #3 below).
  const loadAppData = async (currentUser) => {
    setLoading(true);
    try {
      const fetchedEvents = await fetchAllEvents();
      setEvents(fetchedEvents);

      const fetchedCommunities = await fetchAllCommunities();
      setCommunities(fetchedCommunities);

      if (currentUser && currentUser.uid) {
        const fetchedMatches = await fetchUserMatches(currentUser.uid);
        setMatches(fetchedMatches);
      }
    } catch (err) {
      console.warn("Error loading data from Firestore:", err);
    } finally {
      setLoading(false);
    }
  };

  // --- HANDLERS FOR CREATION AND INTERACTION ---
  // Each handler writes to Firestore first, then updates local state with
  // whatever the write returned (e.g. the new document's generated id) so
  // the UI reflects the persisted version rather than a locally-guessed one.
  const handleCreateEvent = async (newEventData) => {
    try {
      const createdEvent = await createEventInFirestore(newEventData);
      setEvents((prev) => [createdEvent, ...prev]);
    } catch (error) {
      console.error("Failed to create event:", error);
    }
  };

  const handleCreateCommunity = async (newCommunityData) => {
    try {
      const createdCommunity = await createCommunity(newCommunityData);
      setCommunities((prev) => [createdCommunity, ...prev]);
    } catch (error) {
      console.error("Failed to create community:", error);
    }
  };

  const handleAddMatch = async (newMatch) => {
    // Normalize the id/uid pair up front so every match in state has both,
    // regardless of which one the caller happened to provide.
    const matchId = String(newMatch.id || newMatch.uid || Date.now());
    const formattedMatch = {
      ...newMatch,
      id: matchId,
      uid: matchId,
    };

    // Guard against adding the same match twice, then update local state
    // immediately (optimistic update) before persisting to Firestore.
    if (!matches.some((m) => m.id === matchId)) {
      setMatches((prev) => [...prev, formattedMatch]);
      if (auth.currentUser) {
        try {
          await saveMatchToFirestore(auth.currentUser.uid, formattedMatch);
        } catch (error) {
          console.error("Error saving match to Firestore:", error);
        }
      }
    }
  };

  // --- HANDLER: Join or leave an event ---
  // Optimistic update: local userData.joinedEvents flips immediately so the
  // UI (join buttons, counts) responds instantly, then the same change is
  // persisted to Firestore in the background. If the Firestore write fails,
  // the local flip is rolled back so the UI doesn't end up showing a
  // "joined" state that was never actually saved.
  const handleToggleJoinEvent = async (eventId) => {
    const currentJoined = userData.joinedEvents || [];
    const isAlreadyJoined = currentJoined.includes(eventId);

    const updatedEvents = isAlreadyJoined
      ? currentJoined.filter((id) => id !== eventId)
      : [...currentJoined, eventId];

    setUserData((prev) => ({
      ...prev,
      joinedEvents: updatedEvents,
    }));

    if (auth.currentUser) {
      try {
        await toggleEventAttendance(
          auth.currentUser.uid,
          eventId,
          !isAlreadyJoined
        );
      } catch (error) {
        console.error("Error updating joined events in Firestore:", error);
        // Roll back the optimistic update — the write didn't actually land.
        setUserData((prev) => ({
          ...prev,
          joinedEvents: currentJoined,
        }));
      }
    }
  };

  // --- EFFECTS ---

  // 1. Force Splash screen duration (e.g., 4.5 seconds) before moving to the resolved app state
  useEffect(() => {
    const splashTimer = setTimeout(() => {
      if (pendingNextState) {
        setAppState(pendingNextState);
      } else {
        setAppState("auth"); // Fallback if auth check takes too long
      }
    }, 4500);

    return () => clearTimeout(splashTimer);
  }, [pendingNextState]);

  // 2. Firebase Auth State Observer
  // This is the app's real router: depending on whether a user is logged
  // in, whether their Firestore profile document exists, and whether it
  // already has interests saved, it decides which of "auth" / "onboarding"
  // / "main" the app should land on once the splash timer (effect #1)
  // finishes.
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const userRef = doc(db, "users", user.uid);
          const userSnap = await getDoc(userRef);

          if (userSnap.exists()) {
            // Returning user with a saved profile: hydrate userData from
            // Firestore, then decide onboarding vs. main based on whether
            // they've picked any interests yet.
            const profileData = userSnap.data();
            setUserData({
              ...profileData,
              uid: user.uid,
              email: user.email || profileData.email || "",
              avatar: profileData.avatar || "Mateo",
              avatarStyle:
                profileData.avatarStyle || profileData.style || "open-peeps",
              joinedEvents: profileData.joinedEvents || [],
              joinedCommunities: profileData.joinedCommunities || [],
            });

            const fetchedMatches = await fetchUserMatches(user.uid);
            setMatches(fetchedMatches);

            if (profileData.interests && profileData.interests.length > 0) {
              loadAppData(user);
              setPendingNextState("main");
            } else {
              setPendingNextState("onboarding");
            }
          } else {
            // Authenticated but no Firestore profile yet (e.g. first-time
            // Google sign-in): start with a blank profile and send them to
            // onboarding to fill it in.
            setUserData({
              ...BLANK_USER_DATA,
              uid: user.uid,
              email: user.email || "",
            });
            setPendingNextState("onboarding");
          }
        } catch (err) {
          console.error("Error fetching user profile:", err);
          setPendingNextState("onboarding");
        }
      } else {
        // Signed out: reset to a blank profile and send the user to auth.
        setUserData(BLANK_USER_DATA);
        setMatches([]);
        setPendingNextState("auth");
      }
    });

    return () => unsubscribe();
  }, []);

  // 3. Trigger initial app data load when entering main view
  useEffect(() => {
    if (appState === "main" && events.length === 0) {
      loadAppData(auth.currentUser);
    }
  }, [appState]);

  // --- PROFILE SETUP CONFIGURATION ---
  // Called when the user finishes the onboarding wizard: marks onboarding
  // as done locally, loads the main app data, switches to the main screen,
  // and persists the completed profile to Firestore (merge: true so this
  // never wipes out fields written elsewhere).
  const handleCompleteSetup = () => {
    loadAppData(auth.currentUser);
    setAppState("main");

    if (auth.currentUser) {
      const userRef = doc(db, "users", auth.currentUser.uid);
      const payload = {
        ...userData,
        avatar: userData.avatar || "Mateo",
        avatarStyle: userData.avatarStyle || "open-peeps",
      };
      setDoc(userRef, payload, { merge: true });
    }
  };

  // Signs out of Firebase and resets state back to a blank profile so
  // nothing from the previous session leaks into the next login.
  const handleLogout = () => {
    auth.signOut();
    setUserData(BLANK_USER_DATA);
    setMatches([]);
    setAppState("auth");
  };

  return {
    appState,
    setAppState,
    userData,
    setUserData,
    events,
    communities,
    matches,
    loading,
    handleCreateEvent,
    handleCreateCommunity,
    handleAddMatch,
    handleToggleJoinEvent,
    handleCompleteSetup,
    handleLogout,
  };
}
